import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Radio,
  Checkbox,
  Input,
  Typography,
  Alert,
  Tag,
  Progress,
  Modal,
  Row,
  Col,
  Space,
  Divider,
  Spin,
  message,
  Upload,
} from 'antd';
import {
  CameraOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  LeftOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { overrideChecklist } from '../../api/checklist';
import { getS3SignedUrlAndUpload } from '../../api/s3upload';

// Types based on the Kotlin implementation
interface QCOption {
  code: string;
  option: string;
  description: string[];
  is_expected_answer?: boolean;
  reference_image_list: string[];
  image_capture_details: ImageCaptureDetail[];
}

interface ImageCaptureDetail {
  name: string;
  url?: string;
  path?: string;
  imageRequired: boolean;
}

interface QCQuestion {
  action_id: string;
  type: string;
  config: {
    question: string;
    description: string[];
    option_type: 'single' | 'multiple' | 'input';
    options: QCOption[];
    image_capture_details: ImageCaptureDetail[];
    reference_image_list: string[];
  };
  control: {
    visibility: string;
    action_type?: string;
  };
  ui?: {
    title?: string;
    pretty_name?: string;
  };
  pre_conditions?: any[];
}

interface QCGroup {
  action_id: string;
  type: string;
  ui: {
    pretty_name: string;
    title?: string;
  };
  branch_steps?: {
    steps: QCQuestion[];
    qc_success_config?: any[];
  };
  post_conditions: Array<{
    result_expression: Array<Array<{
      code: string;
      result: string;
    }>>;
    action: string;
  }>;
  control: {
    visibility: string;
    action_type?: string;
  };
  pre_conditions?: any[];
}

interface QCData {
  action_id: string;
  type: string;
  ui: {
    pretty_name: string;
    title?: string;
  };
  branch_steps?: {
    steps: QCGroup[];
    qc_success_config?: any[];
  };
  post_conditions: Array<{
    result_expression: Array<Array<{
      code: string;
      result: string;
    }>>;
    action: string;
  }>;
  control: {
    visibility: string;
  };
  config?: any;
  evaluation_criteria?: string;
}

interface QCAnswer {
  questionId: string;
  selectedOptions: string[];
  textInput?: string;
  capturedImages: string[];
}

interface SummaryViewOverride {
  isValid: boolean;
  title: string;
  questions: Array<{ key: string; text: string; isCorrect: boolean }>;
  isViewOnly: boolean;
}

interface QCChecklistProps {
  configData: QCData[];
  onSubmit: (result: { qc_output: any[]; checklist_result: any }) => void;
  onClose: () => void;
  overridePermission?: boolean;
  viewMode?: boolean;
  qcOutputData?: any[];
  qcStatus?: string;
}

// Get all questions from the nested structure
const getAllQuestions = (data: QCData[]): QCQuestion[] => {
  const questions: QCQuestion[] = [];
  if (!data || !Array.isArray(data)) {
    return questions;
  }
  data.forEach(group => {
    if (group.branch_steps && group.branch_steps.steps) {
      group.branch_steps.steps.forEach(subGroup => {
        if (subGroup.branch_steps?.steps) {
          questions.push(...subGroup.branch_steps.steps);
        }
      });
    }
  });
  return questions;
};

const isQuestionMandatory = (question: QCQuestion): boolean => {
  return question.control?.visibility === 'mandatory';
};
// Extract questions from qc_output structure (used in viewMode)
const getQuestionsFromOutput = (qcOutput: any[]): any[] => {
  const questions: any[] = [];
  if (!qcOutput || !Array.isArray(qcOutput)) {
    return questions;
  }
  const traverse = (items: any[]) => {
    items.forEach((item) => {
      if (item.type === 'question') {
        questions.push(item);
      }
      if (Array.isArray(item.childOutput) && item.childOutput.length > 0) {
        traverse(item.childOutput);
      }
    });
  };
  traverse(qcOutput);
  return questions;
};

const QCChecklistComponent: React.FC<QCChecklistProps> = ({
  configData: qcData = [],
  onSubmit,
  onClose,
  overridePermission = false,
  viewMode = false,
  qcOutputData,
  qcStatus,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<QCAnswer[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);

  const allQuestions = getAllQuestions(qcData);
  const currentQuestion = allQuestions[currentQuestionIndex];
  const totalQuestions = allQuestions.length;

  // Initialize answers when questions are available
  React.useEffect(() => {
    if (allQuestions.length > 0 && answers.length === 0) {
      const initialAnswers = allQuestions.map(q => ({
        questionId: q.action_id,
        selectedOptions: [],
        textInput: '',
        capturedImages: [],
      }));
      setAnswers(initialAnswers);
    }
  }, [allQuestions, answers.length]);

  const handleAnswerChange = (
    questionId: string,
    value: string | string[],
    isTextInput = false,
  ) => {
    setAnswers(prev => prev.map(answer =>
      answer.questionId === questionId
        ? {
            ...answer,
            [isTextInput ? 'textInput' : 'selectedOptions']: Array.isArray(value) ? value : [value],
          }
        : answer,
    ));
  };

  const handleImageCapture = (questionId: string, imageUrl: string) => {
    setAnswers(prev => prev.map(answer =>
      answer.questionId === questionId
        ? {
            ...answer,
            capturedImages: [...answer.capturedImages, imageUrl],
          }
        : answer,
    ));
  };

  const removeImage = (questionId: string, imageUrl: string) => {
    setAnswers(prev => prev.map(answer =>
      answer.questionId === questionId
        ? {
            ...answer,
            capturedImages: answer.capturedImages.filter(img => img !== imageUrl),
          }
        : answer,
    ));
  };

  const getCurrentAnswer = (questionId: string): QCAnswer | undefined => {
    return answers.find(a => a.questionId === questionId);
  };

  const isQuestionAnswered = (question: QCQuestion): boolean => {
    const answer = getCurrentAnswer(question.action_id);
    if (!answer) return false;

    // Check if question has required images
    const hasRequiredImages = question.config.image_capture_details.length > 0;

    // If images are required, check if at least one image is uploaded
    if (hasRequiredImages) {
      if (!answer.capturedImages || answer.capturedImages.length === 0) {
        return false;
      }
    }

    // Check answer based on option type
    if (question.config.option_type === 'input') {
      return typeof answer.textInput === 'string' && answer.textInput.trim() !== '';
    }
    return answer.selectedOptions.length > 0;
  };

  const getExpectedAnswerForQuestion = (questionId: string): string | null => {
    if (!qcData.length) return null;

    // Look for the expected answer in the post_conditions of the question group
    for (const group of qcData) {
      if (group.branch_steps && group.branch_steps.steps) {
        for (const subGroup of group.branch_steps.steps) {
          if (subGroup.post_conditions) {
            for (const postCondition of subGroup.post_conditions) {
              if (postCondition.result_expression) {
                for (const expression of postCondition.result_expression) {
                  for (const condition of expression) {
                    if (condition.code === questionId) {
                      return condition.result;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  const validateQualityCheck = (): { isValid: boolean; status: string } => {
    if (!qcData.length) return { isValid: false, status: 'failed' };

    // Check each question against its expected answer from post_conditions
    for (const question of allQuestions) {
      const expectedAnswer = getExpectedAnswerForQuestion(question.action_id);
      if (expectedAnswer) {
        const answer = getCurrentAnswer(question.action_id);
        if (!answer || !answer.selectedOptions.includes(expectedAnswer)) {
          return { isValid: false, status: 'failed' };
        }
      }
    }

    return { isValid: true, status: 'success' };
  };

  const handleSubmitQC = () => {
    setShowConfirmation(true);
  };

  const handleConfirmProceed = () => {
    setShowConfirmation(false);
    setShowSummary(true);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleFinalSubmit = async () => {
    const qcOutput = transformAnswersToQCOutput(answers);
    const checklistResult = evaluateChecklistResult(qcOutput);
    setSubmitting(true);

    try {
      await onSubmit({
        qc_output: qcOutput,
        checklist_result: checklistResult,
      });
    } catch (err) {
      setError('Failed to submit QC checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const evaluateChecklistResult = (qcOutput) => {
    if (!qcOutput || !Array.isArray(qcOutput) || qcOutput.length === 0) {
      return {
        overall_status: 'failed',
        passed: false,
        total_questions: 0,
        passed_questions: 0,
        failed_questions: 0,
        success_rate: 0,
        failed_question_details: [],
        failed_group_details: [],
        summary: 'No QC data available',
        checklist_metadata: {
          total_groups: 0,
          failed_groups: 0,
          evaluation_timestamp: new Date().toISOString(),
        },
      };
    }

    let totalQuestions = 0;
    let passedQuestions = 0;
    const failedQuestions = [];
    const failedGroups = [];

    // Recursive function to traverse the QC output structure
    const traverseQCOutput = (items, parentPath = '') => {
      items.forEach(item => {
        const currentPath = parentPath ? `${parentPath}.${item.action_id}` : item.action_id;

        if (item.type === 'question') {
          totalQuestions++;
          
          if (item.output.isFailed || item.status === 'failed') {
            failedQuestions.push({
              action_id: item.action_id,
              question: item.output.questionDescription?.[0] || 'Unknown question',
              selected_answer: item.output.answers?.[0]?.code || 'No answer',
              expected_answer: getExpectedAnswerForQuestion(item.action_id),
              path: currentPath,
              option_type: item.output.option_type,
            });
          } else {
            passedQuestions++;
          }
        } else if (item.type === 'question_group') {
          // Check if this group failed based on multiple criteria
          const groupFailed = item.output.isFailed ||
                             item.status === 'failed' ||
                             (item.output.answers?.[0]?.code === 'FAIL');
          
          if (groupFailed) {
            failedGroups.push({
              action_id: item.action_id,
              group_name: item.output.questionDescription?.[0] || 'Unknown group',
              group_code: item.output.answers?.[0]?.code || 'Unknown',
              path: currentPath,
              failed_questions_count: item.childOutput ?
                countFailedQuestionsInGroup(item.childOutput) : 0,
            });
          }
        }

        // Recursively check child outputs
        if (item.childOutput && item.childOutput.length > 0) {
          traverseQCOutput(item.childOutput, currentPath);
        }
      });
    };

    // Helper function to count failed questions in a group
    const countFailedQuestionsInGroup = (childItems) => {
      let failedCount = 0;
      childItems.forEach(child => {
        if (child.type === 'question' && (child.output.isFailed || child.status === 'failed')) {
          failedCount++;
        }
        if (child.childOutput && child.childOutput.length > 0) {
          failedCount += countFailedQuestionsInGroup(child.childOutput);
        }
      });
      return failedCount;
    };

    traverseQCOutput(qcOutput);

    // Determine overall status
    const hasFailedQuestions = failedQuestions.length > 0;
    const hasFailedGroups = failedGroups.length > 0;
    const overallPassed = !hasFailedQuestions && !hasFailedGroups;

    // Calculate success rate
    const successRate = totalQuestions > 0 ? (passedQuestions / totalQuestions) * 100 : 0;

    // Generate detailed summary
    let summary = '';
    if (overallPassed) {
      summary = `All ${totalQuestions} questions passed successfully. Checklist completed without issues.`;
    } else {
      const failureReasons = [];
      if (failedQuestions.length > 0) {
        failureReasons.push(`${failedQuestions.length} question${failedQuestions.length > 1 ? 's' : ''} failed`);
      }
      if (failedGroups.length > 0) {
        failureReasons.push(`${failedGroups.length} group${failedGroups.length > 1 ? 's' : ''} failed`);
      }
      summary = `Checklist failed: ${failureReasons.join(' and ')} out of ${totalQuestions} total questions.`;
    }

    return {
      overall_status: overallPassed ? 'passed' : 'failed',
      passed: overallPassed,
      total_questions: totalQuestions,
      passed_questions: passedQuestions,
      failed_questions: failedQuestions.length,
      success_rate: Math.round(successRate * 100) / 100, // Round to 2 decimal places
      failed_question_details: failedQuestions,
      failed_group_details: failedGroups,
      summary,
      checklist_metadata: {
        total_groups: qcOutput.length,
        failed_groups: failedGroups.length,
        evaluation_timestamp: new Date().toISOString(),
        qc_structure_validation: {
          has_proper_structure: qcOutput.every(group =>
            group.type === 'question_group' &&
            group.output &&
            group.output.answers &&
            Array.isArray(group.output.answers),
          ),
          total_nested_levels: calculateMaxNestingLevel(qcOutput),
        },
      },
    };
  };

  // Helper function to calculate maximum nesting level
  const calculateMaxNestingLevel = (items, currentLevel = 1) => {
    let maxLevel = currentLevel;
    items.forEach(item => {
      if (item.childOutput && item.childOutput.length > 0) {
        const childLevel = calculateMaxNestingLevel(item.childOutput, currentLevel + 1);
        maxLevel = Math.max(maxLevel, childLevel);
      }
    });
    return maxLevel;
  };

  const transformAnswersToQCOutput = (answers) => {
    if (!qcData || !Array.isArray(qcData)) {
      return [];
    }

    return qcData.map(group => {
      const groupOutput = {
        type: 'question_group',
        output: {
          answers: [{ code: 'RVP-0' }], // Add answers field to match structure
          action_id: group.action_id,
          option_type: 'question_group',
          questionDescription: ['Default description'],
        },
        status: 'success',
        action_id: group.action_id,
        childOutput: [],
      };

      // Process sub-groups
      if (group.branch_steps && group.branch_steps.steps) {
        groupOutput.childOutput = group.branch_steps.steps.map(subGroup => {
          const subGroupOutput = {
            type: 'question_group',
            output: {
              answers: [{ code: 'PASS' }], // Default sub-group answer
              isFailed: false,
              action_id: subGroup.action_id,
              option_type: 'question_group',
              questionDescription: ['Default description'],
            },
            status: 'success',
            action_id: subGroup.action_id,
            childOutput: [],
          };

          // Process individual questions
          if (subGroup.branch_steps && subGroup.branch_steps.steps) {
            subGroupOutput.childOutput = subGroup.branch_steps.steps.map(question => {
              const answer = answers.find(a => a.questionId === question.action_id);
              const selectedCode = answer?.selectedOptions[0] || '';
              const isFailed = isQuestionFailedInTransform(question.action_id, selectedCode);

              return {
                type: 'question',
                output: {
                  answers: [{ code: selectedCode }],
                  isFailed,
                  action_id: question.action_id,
                  option_type: question.config.option_type,
                  questionDescription: question.config.description || [],
                  image_capture_details: answer?.capturedImages || [],
                },
                status: isFailed ? 'failed' : 'success',
                action_id: question.action_id,
                childOutput: [],
              };
            });

            // Update sub-group status based on question results
            const hasFailedQuestions = subGroupOutput.childOutput.some(q => q.output.isFailed);
            subGroupOutput.output.isFailed = hasFailedQuestions;
            subGroupOutput.status = hasFailedQuestions ? 'failed' : 'success';
            if (hasFailedQuestions) {
              subGroupOutput.output.answers = [{ code: 'FAIL' }];
            }
          }

          return subGroupOutput;
        });

        // Update group status based on sub-group results
        const hasFailedSubGroups = groupOutput.childOutput.some(sg => sg.output.isFailed);
        groupOutput.status = hasFailedSubGroups ? 'failed' : 'success';
        // Update top-level group answers based on overall status
        if (hasFailedSubGroups) {
          groupOutput.output.answers = [{ code: 'FAIL' }];
        }
      }

      return groupOutput;
    });
  };

  const isQuestionFailedInTransform = (questionId, selectedAnswer) => {
    if (!qcData || !selectedAnswer) {
      return true;
    }

    // Look for the expected answer in the post_conditions
    for (const group of qcData) {
      if (group.branch_steps && group.branch_steps.steps) {
        for (const subGroup of group.branch_steps.steps) {
          if (subGroup.post_conditions) {
            for (const postCondition of subGroup.post_conditions) {
              if (postCondition.result_expression) {
                for (const expression of postCondition.result_expression) {
                  for (const condition of expression) {
                    if (condition.code === questionId) {
                      return selectedAnswer !== condition.result;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    return false;
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const canProceed = (): boolean => {
    if (!currentQuestion) return false;
    if (!isQuestionMandatory(currentQuestion)) return true;
    return isQuestionAnswered(currentQuestion);
  };

  const allQuestionsAnswered = (): boolean => {
    return allQuestions.every(q =>
      !isQuestionMandatory(q) || isQuestionAnswered(q),
    );
  };

  const renderConfirmationModal = () => {
    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: '#faad14',
              color: 'white',
              fontSize: 14,
              fontWeight: 'bold',
            }}
            >
              !
            </span>
            Attention
          </div>
        }
        visible={true}
        onCancel={handleCancelConfirmation}
        footer={null}
        width={400}
        centered
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div style={{ padding: '12px 0' }}>
          <Typography.Title level={4} style={{ margin: 0, marginBottom: 8 }}>
            Are you sure you want to proceed?
          </Typography.Title>
          <Typography.Text type="secondary">
            This action can't undone.
          </Typography.Text>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 32 }}>
            <Button
              onClick={handleCancelConfirmation}
              style={{
                borderRadius: 6,
                height: 40,
                paddingLeft: 24,
                paddingRight: 24,
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleConfirmProceed}
              style={{
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                borderRadius: 6,
                height: 40,
                paddingLeft: 24,
                paddingRight: 24,
              }}
            >
              Yes!
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const renderSummaryView = (overrideData?: SummaryViewOverride) => {
    // When overrideData is provided (viewMode), use it directly.
    // Otherwise, compute from component state (existing interactive behavior).
    const isValid = overrideData
      ? overrideData.isValid
      : validateQualityCheck().isValid;

    const summaryTitle = overrideData
      ? overrideData.title
      : (qcData.find(item => item.type === 'question_summary')?.ui?.pretty_name
         || 'Gate In Checklist - Summary');

    const isViewOnly = overrideData?.isViewOnly ?? false;

    const questionItems = overrideData
      ? overrideData.questions
      : allQuestions.map(question => {
          const answer = getCurrentAnswer(question.action_id);
          const expectedAnswer = getExpectedAnswerForQuestion(question.action_id);
          return {
            key: question.action_id,
            text: question.config.question,
            isCorrect: !!(expectedAnswer && answer?.selectedOptions.includes(expectedAnswer)),
          };
        });

    return (
      <Modal
        title={summaryTitle}
        visible={true}
        onCancel={onClose}
        footer={null}
        width={600}
        centered
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div style={{ padding: '20px 0' }}>
          {/* Success/Failure Icon and Message */}
          <div style={{ marginBottom: 20, textAlign: 'center' }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: isValid ? '#52c41a' : '#ff4d4f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              {isValid ? (
                <CheckCircleOutlined style={{ fontSize: 32, color: 'white' }} />
              ) : (
                <CloseCircleOutlined style={{ fontSize: 32, color: 'white' }} />
              )}
            </div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Quality check {isValid ? 'passed' : 'failed'}
            </Typography.Title>
          </div>

          {/* Questions Summary Table */}
          <div style={{ marginBottom: 32 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 16px',
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #d9d9d9',
              fontWeight: 600,
              borderRadius: '6px 6px 0 0',
            }}>
              <span>Question</span>
              <span>Result</span>
            </div>

            {questionItems.map((item, index) => {
              return (
                <div
                  key={item.key}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: index < questionItems.length - 1 ? '1px solid #f0f0f0' : '1px solid #d9d9d9',
                    alignItems: 'center',
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #d9d9d9',
                    borderRight: '1px solid #d9d9d9',
                    ...(index === questionItems.length - 1 && { borderRadius: '0 0 6px 6px' }),
                  }}
                >
                  <span style={{ textAlign: 'left', flex: 1, fontSize: 14 }}>
                    {item.text}
                  </span>
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: item.isCorrect ? '#52c41a' : '#ff4d4f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {item.isCorrect ? (
                      <CheckCircleOutlined style={{ fontSize: 12, color: 'white' }} />
                    ) : (
                      <CloseCircleOutlined style={{ fontSize: 12, color: 'white' }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'right', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            {/* Show Override button only when checklist failed and user has permission */}
            {!isViewOnly && !isValid && overridePermission && (
              <Button
                type="default"
                size="large"
                style={{
                  minWidth: 160,
                  height: 40,
                  borderRadius: 6,
                  color: '#1890ff',
                  border: 'none',
                }}
                onClick={handleFinalSubmit}
                loading={overriding}
              >
                Override & Continue
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              style={{
                minWidth: 120,
                height: 40,
                borderRadius: 6,
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
              }}
              onClick={isViewOnly ? onClose : (isValid ? handleFinalSubmit : onClose)}
              loading={!isViewOnly && submitting}
            >
              {isViewOnly ? 'Okay' : (isValid ? 'Continue' : 'Okay')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  // View mode: render summary directly from qc_output data
  if (viewMode && qcOutputData) {
    const outputQuestions = getQuestionsFromOutput(qcOutputData);
    return renderSummaryView({
      isValid: qcStatus === 'success',
      title: 'Checklist Result',
      questions: outputQuestions.map(q => ({
        key: q.action_id,
        text: q.output?.questionDescription?.[0] || 'No description',
        isCorrect: q.status === 'success' && !q.output?.isFailed,
      })),
      isViewOnly: true,
    });
  }

  if (loading) {
    return (
      <div style={{ width: '100%', padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <Typography.Title level={4} style={{ marginTop: 16 }}>
          Loading QC Details...
        </Typography.Title>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: 16 }}
        action={
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (!qcData.length || !currentQuestion) {
    return (
      <Alert
        message="No QC questions found"
        description="No QC questions found for the selected references."
        type="info"
        showIcon
        style={{ margin: 16 }}
      />
    );
  }

  // Show confirmation modal when submit is clicked
  if (showConfirmation) {
    return renderConfirmationModal();
  }

  // Show summary view when all questions are answered and confirmed
  if (showSummary) {
    return renderSummaryView();
  }

  const renderQuestionOptions = (question: QCQuestion) => {
    const answer = getCurrentAnswer(question.action_id);

    switch (question.config.option_type) {
      case 'single':
        return (
          <Radio.Group
            value={answer?.selectedOptions[0] || ''}
            onChange={(e) => handleAnswerChange(question.action_id, e.target.value)}
          >
            <Space direction="horizontal" >
              {question.config.options.map((option) => (
                <Radio key={option.code} value={option.code}>
                  <Space>
                    {option.option}
                    {option.is_expected_answer && (
                      <Tag color="success">Expected</Tag>
                    )}
                  </Space>
                  {option.description && option.description.map((desc, idx) => (
                    <div key={idx}>
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        {desc}
                      </Typography.Text>
                    </div>
                  ))}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      case 'multiple':
        return (
          <Checkbox.Group
            value={answer?.selectedOptions || []}
            onChange={(checkedValues) =>
              handleAnswerChange(question.action_id, checkedValues as string[])
            }
          >
            <Space direction="vertical">
              {question.config.options.map((option) => (
                <Checkbox key={option.code} value={option.code}>
                  <Space>
                    {option.option}
                    {option.is_expected_answer && (
                      <Tag color="success">Expected</Tag>
                    )}
                  </Space>
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );

      case 'input':
        return (
          <Input.TextArea
            rows={3}
            placeholder="Enter your response..."
            value={answer?.textInput || ''}
            onChange={(e) => handleAnswerChange(question.action_id, e.target.value, true)}
          />
        );

      default:
        return null;
    }
  };

  const renderReferenceImages = (question: QCQuestion) => {
    if (!question.config.reference_image_list.length) return null;

    return (
      <div style={{ marginTop: 16 }}>
        <Typography.Text strong>Reference Images:</Typography.Text>
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          {question.config.reference_image_list.map((imageUrl, idx) => (
            <Col key={idx}>
              <div
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedImageUrl(imageUrl);
                  setShowImageDialog(true);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedImageUrl(imageUrl);
                    setShowImageDialog(true);
                  }
                }}
              >
                <img
                  src={imageUrl}
                  alt={`Reference ${idx + 1}`}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 4,
                  }}
                />
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderCapturedImages = (question: QCQuestion) => {
    const answer = getCurrentAnswer(question.action_id);
    if (!answer?.capturedImages.length) return null;

    return (
      <div style={{ marginTop: 16 }}>
        <Typography.Text strong>Captured Images:</Typography.Text>
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          {answer.capturedImages.map((imageUrl, idx) => (
            <Col key={idx} style={{ position: 'relative' }}>
              <img
                src={imageUrl}
                alt={`Captured ${idx + 1}`}
                style={{
                  width: 80,
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 4,
                }}
              />
              <Button
                type="primary"
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                style={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  minWidth: 24,
                  height: 24,
                  padding: 0,
                }}
                onClick={() => removeImage(question.action_id, imageUrl)}
              />
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  return (
    <Modal
      title={(() => {
        // Find the question_group item for the title
        const questionGroupConfig = qcData.find(item => item.type === 'question_group');
        return questionGroupConfig?.ui?.pretty_name || 'Gate In Checklist';
      })()}
      visible={true}
      onCancel={onClose}
      footer={null}
      width={500}
      centered
      bodyStyle={{ padding: '20px 16px' }}
    >
      <div>
        {/* Question */}
        <div style={{ marginBottom: 24 }}>
          <Typography.Text style={{ fontSize: 16, color: '#000' }}>
            {currentQuestion.config.question}
            {isQuestionMandatory(currentQuestion) && (
              <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
            )}
          </Typography.Text>
        </div>

        {/* Question Description */}
        {currentQuestion.config.description.map((desc, idx) => (
          <Typography.Paragraph key={idx} type="secondary" style={{ marginBottom: 16 }}>
            {desc}
          </Typography.Paragraph>
        ))}

        {/* Question Options */}
        <div style={{ marginBottom: 24 }}>
          {renderQuestionOptions(currentQuestion)}
        </div>

        {renderReferenceImages(currentQuestion)}

        {currentQuestion.config.image_capture_details.length > 0 && (
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Typography.Text style={{ fontSize: 16, color: '#000' }}>
                Add Photo
                {isQuestionMandatory(currentQuestion) && (
                  <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
                )}
              </Typography.Text>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={async (file) => {
                  const questionId = currentQuestion.action_id;
                  setUploadingImage(questionId);
                  
                  try {
                    const uploadResult = await getS3SignedUrlAndUpload(file);
                    
                    if (uploadResult.isSuccess && uploadResult.url) {
                      handleImageCapture(questionId, uploadResult.url);
                      message.success('Image uploaded successfully');
                    } else {
                      message.error(uploadResult.errorMessage || 'Failed to upload image');
                    }
                  } catch (error) {
                    message.error('Failed to upload image');
                    console.error('Upload error:', error);
                  } finally {
                    setUploadingImage(null);
                  }
                  
                  return false; // Prevent default upload behavior
                }}
              >
                <Button
                  type="primary"
                  icon={<CameraOutlined />}
                  loading={uploadingImage === currentQuestion.action_id}
                  disabled={uploadingImage === currentQuestion.action_id}
                  style={{
                    borderRadius: 6,
                    height: 40,
                    paddingLeft: 16,
                    paddingRight: 16,
                  }}
                >
                  Upload
                </Button>
              </Upload>
            </div>
          </div>
        )}

        {/* Captured Images */}
        {renderCapturedImages(currentQuestion)}

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          <Button
            onClick={previousQuestion}
            disabled={currentQuestionIndex === 0}
            style={{
              borderRadius: 6,
              height: 40,
              paddingLeft: 24,
              paddingRight: 24,
            }}
          >
            Go Back
          </Button>

          <Button
            type="primary"
            onClick={currentQuestionIndex === totalQuestions - 1 ? handleSubmitQC : nextQuestion}
            disabled={!canProceed()}
            style={{
              backgroundColor: '#1890ff',
              borderColor: '#1890ff',
              borderRadius: 6,
              height: 40,
              paddingLeft: 24,
              paddingRight: 24,
            }}
          >
            {currentQuestionIndex === totalQuestions - 1 ? 'Submit' : 'Proceed'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QCChecklistComponent;